/**
 * Description: Merges runtime options with character JSON; supports URL fetch or async characterProvider.
 * Usage: await mergeRuntimeConfig(runtimeConfig, { fetch: globalThis.fetch }).
 */

const DEFAULTS = {
  character: 'default',
  characterBaseUrl: '',
  characterConfigUrl: '',
  movementSpeed: 50,
  behaviorFrequency: 50,
  mobileEnabled: false,
  persistenceEnabled: true,
  pluginUrl: '',
  enabledCapabilities: [],
  customColors: {},
  size: 48,
  capabilities: ['idle', 'walk', 'jump', 'sit', 'fall'],
  limits: {
    gravity: 900,
    walkSpeed: 70,
    walkAccel: 400,
    jumpForce: 320,
    friction: 8,
  },
  states: {
    idle: { minDuration: 2000, maxDuration: 5000 },
    walk: { minDuration: 1500, maxDuration: 5000 },
    jump: {},
    sit: { minDuration: 3000, maxDuration: 7000 },
    fall: {},
  },
  transitions: {
    idle: { walk: 40, jump: 15, sit: 25, idle: 20 },
    walk: { idle: 50, jump: 10, sit: 15, walk: 25 },
    sit: { idle: 60, walk: 30, jump: 10 },
    jump: { idle: 100 },
    fall: { idle: 100 },
  },
};

function cloneDefaults() {
  return JSON.parse(JSON.stringify(DEFAULTS));
}

function applyCharacterData(config, data) {
  if (data.size) config.size = data.size;
  if (data.capabilities) config.capabilities = data.capabilities;
  if (data.limits) {
    config.limits = data.limits;
    config.physics = data.limits;
  }
  if (data.states) config.states = data.states;
  if (data.transitions) config.transitions = data.transitions;
  if (data.colorPalette) config.colorPalette = data.colorPalette;
  if (data.poses) config.poses = data.poses;
  if (data.statePoses) config.statePoses = data.statePoses;
}

/**
 * Description: Builds the fully merged config object used by engines and renderer.
 * Usage: Pass WordPress ndpConfig or library options plus optional characterProvider for remote assets.
 */
export async function mergeRuntimeConfig(runtimeConfig, deps) {
  const fetchFn = deps && deps.fetch ? deps.fetch : typeof fetch !== 'undefined' ? fetch.bind(globalThis) : null;

  const config = cloneDefaults();
  if (runtimeConfig) {
    for (const key in runtimeConfig) {
      if (Object.prototype.hasOwnProperty.call(runtimeConfig, key) && key !== 'characterProvider') {
        config[key] = runtimeConfig[key];
      }
    }
  }

  config.movementSpeed = parseInt(String(config.movementSpeed), 10) || DEFAULTS.movementSpeed;
  config.behaviorFrequency = parseInt(String(config.behaviorFrequency), 10) || DEFAULTS.behaviorFrequency;
  config.mobileEnabled = !!config.mobileEnabled;
  config.persistenceEnabled = !!config.persistenceEnabled;

  if (!config.physics) config.physics = config.limits || DEFAULTS.limits;

  const provider = runtimeConfig && runtimeConfig.characterProvider;

  if (provider && typeof provider.getCharacter === 'function') {
    const result = await provider.getCharacter(config.character);
    if (result && result.config) {
      applyCharacterData(config, result.config);
    }
    if (result && result.baseUrl) {
      config.characterBaseUrl = result.baseUrl;
      config.characterConfigUrl = result.configUrl || result.baseUrl + 'character.json';
    }
    return config;
  }

  if (config.characterConfigUrl && fetchFn) {
    try {
      const res = await fetchFn(config.characterConfigUrl);
      if (res.ok) {
        const data = await res.json();
        applyCharacterData(config, data);
      }
    } catch {
      /* keep defaults */
    }
  }

  return config;
}
